import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { FolderArchive, Upload, Download, ExternalLink, FileText, Search, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const CATEGORIES = {
  SK: "Surat Keputusan (SK)",
  AD_ART: "AD/ART",
  KEPUTUSAN: "Keputusan Rapat",
  HASIL_SIDANG: "Hasil Sidang",
  HASIL_RAKER: "Hasil RAKER",
  PEDOMAN: "Pedoman",
  PROPOSAL: "Proposal",
  TOR: "TOR",
  RAB: "RAB",
  LPJ: "LPJ",
  LAPORAN: "Laporan",
  FORMULIR: "Formulir",
  FOTO: "Foto / Dokumentasi",
};

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function Arsip() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("ALL");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "SK", description: "", year: new Date().getFullYear(), file_base64: "", file_name: "", external_url: "", is_public: false });

  const canUpload = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","WAKIL_SEKRETARIS","KETUA_KLASIS"].includes(user?.role);

  const load = () => {
    setLoading(true);
    api.get("/documents").then((r)=>setItems(r.data)).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const filtered = items.filter((d) => {
    if (category !== "ALL" && d.category !== category) return false;
    if (q && !(d.title + (d.description || "")).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const submit = async () => {
    if (!form.title || !form.category) { toast.error("Judul & kategori wajib."); return; }
    if (!form.file_base64 && !form.external_url) { toast.error("Upload file atau berikan URL."); return; }
    try {
      await api.post("/documents", {
        ...form,
        year: Number(form.year) || null,
        description: form.description || null,
        file_base64: form.file_base64 || null,
        file_name: form.file_name || null,
        external_url: form.external_url || null,
      });
      toast.success("Dokumen berhasil diarsipkan.");
      setOpen(false);
      setForm({ title: "", category: "SK", description: "", year: new Date().getFullYear(), file_base64: "", file_name: "", external_url: "", is_public: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.id}/file`);
      if (data.external_url) {
        window.open(data.external_url, "_blank");
      } else if (data.file_base64) {
        const a = document.createElement("a");
        a.href = data.file_base64;
        a.download = data.file_name || `${doc.title}.pdf`;
        a.click();
      } else {
        toast.info("File tidak tersedia.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Administrasi</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Arsip Dokumen</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">SK, AD/ART, hasil sidang, pedoman, proposal, TOR, RAB, LPJ, dan dokumen resmi Klasis lainnya.</p>
        </div>
        {canUpload && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-upload-doc"><Upload className="h-4 w-4 mr-2" /> Upload Dokumen</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Upload Dokumen Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Judul</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="input-doc-title" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Kategori</Label>
                    <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                      <SelectTrigger data-testid="select-doc-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORIES).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Tahun</Label><Input type="number" value={form.year} onChange={(e)=>setForm({...form, year:e.target.value})} data-testid="input-doc-year" /></div>
                </div>
                <div><Label>Deskripsi</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} /></div>
                <div>
                  <Label>File PDF/gambar (opsional, maks ~6MB)</Label>
                  <Input type="file" accept="application/pdf,image/*" data-testid="input-doc-file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      if (f.size > 6_000_000) { toast.error("File terlalu besar (maks 6MB)"); return; }
                      const b64 = await fileToBase64(f);
                      setForm({...form, file_base64: b64, file_name: f.name});
                    }}
                  />
                  {form.file_name && <div className="text-xs text-muted-foreground mt-1">✓ {form.file_name}</div>}
                </div>
                <div><Label>Atau URL eksternal</Label><Input value={form.external_url} onChange={(e)=>setForm({...form, external_url:e.target.value})} placeholder="https://..." data-testid="input-doc-url" /></div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.is_public} onCheckedChange={(v)=>setForm({...form, is_public:!!v})} data-testid="checkbox-doc-public" />
                  <span className="text-sm">Dapat diakses dari Portal Publik</span>
                </label>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="btn-save-doc">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cari dokumen…" className="pl-9 w-72" data-testid="arsip-search" />
        </div>
      </div>

      <Tabs value={category} onValueChange={setCategory} className="mb-4">
        <TabsList className="flex flex-wrap h-auto" data-testid="arsip-tabs">
          <TabsTrigger value="ALL">Semua</TabsTrigger>
          {Object.entries(CATEGORIES).slice(0,7).map(([k,v]) => <TabsTrigger key={k} value={k} data-testid={`tab-cat-${k}`}>{v.split(" ")[0]}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <FolderArchive className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada dokumen di kategori ini.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d) => (
            <Card key={d.id} className="hover:border-primary/30 transition-colors" data-testid={`doc-${d.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <StatusBadge status="info" label={CATEGORIES[d.category]?.split(" ")[0] || d.category} />
                    {d.is_public && <StatusBadge status="success" label="Publik" />}
                  </div>
                </div>
                <div className="font-heading font-bold text-sm leading-snug">{d.title}</div>
                {d.description && <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{d.description}</div>}
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                  <div className="text-[0.65rem] text-muted-foreground">
                    {d.year || "—"} • {new Date(d.created_at).toLocaleDateString("id-ID")}
                  </div>
                  {(d.external_url !== null || d.file_name) ? (
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={() => download(d)} data-testid={`btn-download-${d.id}`}>
                      {d.external_url ? <ExternalLink className="h-3 w-3 mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                      {d.external_url ? "Buka" : "Unduh"}
                    </Button>
                  ) : (
                    <span className="text-[0.65rem] text-muted-foreground italic">Metadata saja</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
